/**
 * Carte d'un cours (SPEC PL3 à PL6, PL11) : liseré et nom à la couleur de la marque, heure, playlist, « studio ·
 * coach », étiquette d'état ; en vue multi-marques, nom de la marque en haut à droite. Aucun nombre de places.
 *
 * Élément selon la destination : lien interne (fiche cours) ; lien vers le site d'une autre marque, ouvert dans un
 * nouvel onglet par openCrossSite (lib/crossSite.ts : passage de session éventuel), avec l'icône « nouvel onglet » sur
 * l'étiquette d'état et un nom accessible « Réserver chez RIISE, nouvel onglet » ; ouverture par window.open sur ce
 * site (mode coach : le shim du thème réécrit /reservation/) ; ou bloc inerte (cours passé, pas encore ouvert, autre
 * marque sans adresse connue).
 */
import { format } from 'date-fns';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PlanningClass } from '../../api/planning';
import { openCrossSite } from '../../lib/crossSite';
import type { PlanningBrand } from '../../lib/planning/brands';
import type { ClassStatus, ClassTarget } from '../../lib/planning/status';
import { dayLong } from '../../lib/planning/week';
import { IconExternal } from './icons';

export interface ClassCardProps {
  mindbodyClass: PlanningClass;
  status: ClassStatus;
  target: ClassTarget;
  brand: PlanningBrand;
  /** Vue multi-marques : étiquette de marque sur chaque carte (P3). */
  showBrandTag: boolean;
  studioLabel: string;
  /** Mise en page en ligne (liste du jour, mobile). */
  row?: boolean;
  /**
   * Cours réservés du client pas encore connus (client/visits en cours, connecté) : l'étiquette d'état garde sa place
   * mais reste vide, et le nom accessible ne la cite pas, plutôt qu'un « Réserver » qui deviendrait « Réservé ».
   */
  statusPending?: boolean;
}

export function ClassCard({
  mindbodyClass: c,
  status,
  target,
  brand,
  showBrandTag,
  studioLabel,
  row,
  statusPending = false,
}: ClassCardProps) {
  const start = new Date(c.StartDateTime);
  const time = format(start, 'HH:mm');
  const name = (c.ClassDescription?.Name ?? '').trim();
  const theme = (c.ThemeName ?? '').trim();
  const coach = (c.Staff?.FirstName ?? '').trim();
  // « studio · coach » : points médians dessinés en CSS (ux-v2.css .mmv2-dots), masqués en début de ligne. En colonne
  // étroite : « Oberkampf / Patricia », ni point seul en fin de ligne (revue du 04/10) ni en tête de ligne (05/10).
  const metaParts = [studioLabel, coach].filter(Boolean);

  const className = [
    'mmv2-class',
    `mmv2-class--${status.state}`,
    row ? 'mmv2-class--row' : '',
    showBrandTag ? 'mmv2-class--tagged' : '',
    target.kind === 'none' ? 'mmv2-class--inert' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const style = { '--mmv2-class-brand': brand.color, '--mmv2-class-text': brand.textColor } as CSSProperties;

  const external = target.kind === 'external' ? target : null;
  // « Réserver chez RIISE, nouvel onglet » (autre site) ; « Réserver, nouvel onglet » (mode coach).
  const action = external
    ? [external.brandName ? `${status.label} chez ${external.brandName}` : status.label, 'nouvel onglet']
    : target.kind === 'open'
      ? [status.label, 'nouvel onglet']
      : [status.label];
  const label = [
    `${dayLong(start)} à ${time}`,
    name,
    theme,
    studioLabel,
    coach ? `avec ${coach}` : '',
    // La marque est déjà dans « … chez RIISE » quand le lien mène à son site.
    showBrandTag && external?.brandName !== brand.name ? brand.name : '',
    ...(statusPending ? [] : action),
  ]
    .filter(Boolean)
    .join(', ');

  const brandTag = showBrandTag ? <span className="mmv2-brand-tag">{brand.name}</span> : null;
  const nameEl = <span className="mmv2-class__name">{name}</span>;

  const content: ReactNode = (
    <>
      {row ? null : brandTag}
      <span className="mmv2-class__time">{time}</span>
      <span className="mmv2-class__body">
        {row && brandTag ? (
          /*
           * Carte en ligne : nom et marque dans une même rangée, sur toute la largeur à droite de l'heure. Posée dans la
           * colonne de l'étiquette d'état, la marque élargissait cette colonne à « LISTE D’ATTENTE ↗ » sur toutes les
           * lignes et le nom passait sur 2 lignes à côté d'un blanc (revue du 05/10, 360 px).
           */
          <span className="mmv2-class__head">
            {nameEl}
            {brandTag}
          </span>
        ) : (
          nameEl
        )}
        {theme ? <span className="mmv2-class__theme">{theme}</span> : null}
        {metaParts.length ? (
          <span className="mmv2-class__meta mmv2-dots-clip">
            <span className="mmv2-dots">
              {metaParts.map((part, i) => (
                <span key={i} className="mmv2-dots__item">
                  {part}
                </span>
              ))}
            </span>
          </span>
        ) : null}
      </span>
      <span className="mmv2-class__foot">
        <span
          className={`mmv2-class__state mmv2-class__state--${status.tone}`}
          data-pending={statusPending || undefined}
          aria-hidden={statusPending || undefined}
        >
          {target.kind === 'external' || target.kind === 'open' ? (
            // Nouvel onglet (autre marque, ou mode coach) : texte et icône dans une même ligne ; l'icône suit la ligne
            // de base du texte (planning.css).
            <span>
              {status.label}
              <IconExternal className="mmv2-class__state-icon" />
            </span>
          ) : (
            status.label
          )}
        </span>
      </span>
    </>
  );

  const common = {
    className,
    style,
    'data-brand': String(c.SiteId),
    'data-state': status.state,
    'data-class-id': String(c.Id),
  };

  if (target.kind === 'route') {
    return (
      <Link {...common} to={target.to} aria-label={label}>
        {content}
      </Link>
    );
  }

  if (target.kind === 'external') {
    // Clic simple : openCrossSite (onglet ouvert pendant le clic, adresse complétée ensuite). Clic avec une touche
    // (Cmd/Ctrl : onglet en arrière-plan, Maj : fenêtre) et clic du milieu : comportement natif du lien, sans
    // passage de session.
    const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openCrossSite(target.href, target.siteId);
    };
    return (
      <a {...common} href={target.href} target="_blank" rel="noopener" onClick={onClick} aria-label={label}>
        {content}
      </a>
    );
  }

  if (target.kind === 'open') {
    // = screens/Classes/components/Card.tsx : window.open (mode coach, Run Club).
    const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      window.open(target.href, '_blank');
    };
    return (
      <a {...common} href={target.href} target="_blank" rel="noopener" onClick={onClick} aria-label={label}>
        {content}
      </a>
    );
  }

  return (
    <div
      {...common}
      role="group"
      aria-label={label}
      title={status.state === 'nolink' ? `Ce cours se réserve sur le site ${brand.name}.` : undefined}
    >
      {content}
    </div>
  );
}
